// src/pages/vendor/AddDeal.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  MenuItem,
} from '@mui/material';
import { Save, Cancel, LocalOffer } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../../constants';

const AddDeal = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ✅ Get today's date in YYYY-MM-DD format to restrict the calendar
  const today = new Date().toISOString().split('T')[0];

  const [categories, setCategories] = useState([]);
  const [loadingInit, setLoadingInit] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    deal_name: '',
    description: '',
    cate_id: '',
    category_name: '',
    original_price: '',
    discounted_price: '',
    discount_percentage: '',
    expires_at: '', // ✅ Added expires_at to state
    is_active: true,
  });

  // Fetch categories on mount
  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem('vendor_token');
        if (!token) {
          navigate('/vendor/login');
          return;
        }

        const res = await fetch(`${url}/users/get_categories`);
        if (!res.ok) throw new Error('Failed to load categories');
        const data = await res.json();
        setCategories(data || []);
      } catch (e) {
        console.error('Error loading categories', e);
        setError(e.message || 'Failed to initialize form');
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, [navigate]);

  const computeDiscountPercent = (orig, disc) => {
    const o = Number(orig);
    const d = Number(disc);
    if (!o || !d || d >= o) return '';
    return Math.round(((o - d) / o) * 100);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;

    if (field === 'original_price' || field === 'discounted_price') {
      const updated = { ...form, [field]: value };
      const percent = computeDiscountPercent(
        updated.original_price,
        updated.discounted_price
      );
      setForm({ ...updated, discount_percentage: percent });
      return;
    }

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCategorySelect = (e) => {
    const cateId = Number(e.target.value);
    const cat = categories.find((c) => c.cate_id === cateId);
    setForm((prev) => ({
      ...prev,
      cate_id: cateId,
      category_name: cat ? cat.cate_name : '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('vendor_token');
      if (!token) throw new Error('Vendor session expired. Please login again.');

      const body = {
        deal_name: form.deal_name || undefined,
        description: form.description || undefined,
        category: form.category_name || undefined,
        cate_id: form.cate_id !== '' ? Number(form.cate_id) : undefined,
        original_price:
          form.original_price !== '' ? Number(form.original_price) : undefined,
        discounted_price:
          form.discounted_price !== '' ? Number(form.discounted_price) : undefined,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined, // ✅ Include valid date in payload
        is_active: form.is_active,
      };

      const res = await fetch(`${url}/vendors/me/deals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create deal');

      setSuccess('Deal created successfully');
      setTimeout(() => {
        navigate('/vendor/deals/view');
      }, 800);
    } catch (err) {
      setError(err.message || 'Failed to create deal');
    } finally {
      setSaving(false);
    }
  };

  if (loadingInit) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 6 }}>
        <Container maxWidth="sm">
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Loading form...</Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: isMobile ? 2 : 6 }}>
      <Container maxWidth="sm" sx={{ px: isMobile ? 1.5 : 0 }}>
        <Paper
          elevation={3}
          sx={{
            p: isMobile ? 2 : 3,
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{ mb: isMobile ? 1.5 : 2 }}
          >
            <LocalOffer color="primary" fontSize={isMobile ? 'small' : 'medium'} />
            <Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={700}>
                Add New Deal
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', maxWidth: 260 }}
              >
                Create a new deal for your store
              </Typography>
            </Box>
          </Stack>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 1.5 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity="success"
              sx={{ mb: 1.5 }}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={isMobile ? 1.5 : 2}>
              <TextField
                label="Deal name"
                value={form.deal_name}
                onChange={handleChange('deal_name')}
                fullWidth
                required
                size={isMobile ? 'small' : 'medium'}
              />

              <TextField
                label="Description"
                value={form.description}
                onChange={handleChange('description')}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                multiline
                minRows={3}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={isMobile ? 1.5 : 2}
              >
                <TextField
                  select
                  label="Category"
                  value={form.cate_id || ''}
                  onChange={handleCategorySelect}
                  fullWidth
                  required
                  size={isMobile ? 'small' : 'medium'}
                  helperText="Select the category for this deal"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.cate_id} value={cat.cate_id}>
                      {cat.cate_name}
                    </MenuItem>
                  ))}
                </TextField>

                {/* ✅ Valid Upto Date Picker */}
                <TextField
                  fullWidth
                  label="Valid Upto"
                  type="date"
                  value={form.expires_at}
                  onChange={handleChange('expires_at')}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  size={isMobile ? 'small' : 'medium'}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: today, // Prevents past dates
                    style: { cursor: 'pointer' } // Makes it obvious it's clickable
                  }}
                  helperText="Select expiry date"
                />
              </Stack>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={isMobile ? 1.5 : 2}
              >
                <TextField
                  label="Original price"
                  type="number"
                  value={form.original_price}
                  onChange={handleChange('original_price')}
                  fullWidth
                  required
                  size={isMobile ? 'small' : 'medium'}
                  inputProps={{ min: 0 }}
                />
                <TextField
                  label="Discounted price"
                  type="number"
                  value={form.discounted_price}
                  onChange={handleChange('discounted_price')}
                  fullWidth
                  required
                  size={isMobile ? 'small' : 'medium'}
                  inputProps={{ min: 0 }}
                />
              </Stack>

              <TextField
                label="Discount percentage"
                type="number"
                value={form.discount_percentage ?? ''}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                InputProps={{ readOnly: true }}
                helperText="Auto-calculated from prices"
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={isMobile ? 1 : 2}
                sx={{ mt: isMobile ? 0.5 : 1 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  disabled={saving}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    py: isMobile ? 0.7 : 1,
                  }}
                >
                  {saving ? 'Saving...' : 'Create deal'}
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<Cancel />}
                  fullWidth
                  sx={{
                    textTransform: 'none',
                    fontSize: isMobile ? '0.8rem' : '0.9rem',
                    py: isMobile ? 0.7 : 1,
                  }}
                  onClick={() => navigate('/vendor/deals/view')}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AddDeal;